import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../../services/api';
import { Palette, Save, RefreshCw, Upload, Image } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const ThemeSettings = () => {
  const [savingKey, setSavingKey] = useState(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const { data: settings, isLoading, refetch } = useQuery(
    'system-settings',
    adminAPI.getSettings,
    {
      select: (response) => response.data.data.settings
    }
  );

  const handleSaveSetting = async (key, value, description = '') => {
    setSavingKey(key);
    try {
      await adminAPI.updateSetting(key, { value, description });
      toast.success('Theme color updated successfully');
      refetch();
      // Reload the page to apply new colors
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error('Failed to update theme color');
    } finally {
      setSavingKey(null);
    }
  };

  const handleFaviconUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload .ico, .png, .jpg, or .jpeg file');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      toast.error('File size must be less than 1MB');
      return;
    }

    setUploadingFavicon(true);
    const formData = new FormData();
    formData.append('favicon', file);

    try {
      const response = await fetch('/api/theme/favicon', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Favicon uploaded successfully! Refreshing page...');
        // Reload page to show new favicon
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast.error(data.message || 'Failed to upload favicon');
      }
    } catch (error) {
      console.error('Favicon upload error:', error);
      toast.error('Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
      event.target.value = ''; // Reset file input
    }
  };

  const ColorSettingItem = ({ settingKey, title, description, defaultColor }) => {
    const [value, setValue] = useState(settings?.[settingKey]?.value || defaultColor);
    const [isEditing, setIsEditing] = useState(false);

    React.useEffect(() => {
      setValue(settings?.[settingKey]?.value || defaultColor);
    }, [settings, settingKey, defaultColor]);

    const handleSave = () => {
      handleSaveSetting(settingKey, value, description);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setValue(settings?.[settingKey]?.value || defaultColor);
      setIsEditing(false);
    };

    return (
      <div style={{ 
        border: '1px solid #4a4a4a', 
        borderRadius: '8px', 
        padding: '16px',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
          <div>
            <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#ffffff', marginBottom: '4px' }}>{title}</h4>
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>{description}</p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                color: '#3b198e',
                fontSize: '13px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
              <input
                type="color"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #4a4a4a',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent'
                }}
              />
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="#000000"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '4px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSave}
                disabled={savingKey === settingKey}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 16px',
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: savingKey === settingKey ? 'not-allowed' : 'pointer',
                  opacity: savingKey === settingKey ? 0.6 : 1
                }}
              >
                {savingKey === settingKey ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Save className="h-3 w-3" />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={handleCancel}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#9ca3af',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: '12px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              backgroundColor: '#0a0a0a',
              borderRadius: '6px',
              padding: '8px 12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                backgroundColor: value,
                border: '1px solid #4a4a4a',
                borderRadius: '4px'
              }} />
              <span style={{ fontSize: '14px', color: '#ffffff', fontFamily: 'monospace' }}>
                {value || <em style={{ color: '#9ca3af' }}>Not set</em>}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '500', color: '#ffffff', marginBottom: '4px' }}>Theme Settings</h2>
          <p style={{ fontSize: '14px', color: '#9ca3af' }}>
            Customize the color scheme of your reseller panel
          </p>
        </div>
        <button
          onClick={() => refetch()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: 'transparent',
            color: '#9ca3af',
            border: '1px solid #4a4a4a',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Favicon Upload Section */}
      <div style={{ 
        border: '1px solid #4a4a4a', 
        borderRadius: '8px', 
        padding: '16px',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Image className="h-5 w-5" style={{ color: '#3b198e' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff' }}>Browser Tab Icon (Favicon)</h3>
        </div>
        <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>
          Upload a custom icon to display in browser tabs. Accepts .ico, .png, .jpg, or .jpeg files (max 1MB).
        </p>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label
            htmlFor="favicon-upload"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: uploadingFavicon ? '#1a1a1a' : '#3b198e',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: uploadingFavicon ? 'not-allowed' : 'pointer',
              opacity: uploadingFavicon ? 0.6 : 1
            }}
          >
            {uploadingFavicon ? (
              <>
                <LoadingSpinner size="sm" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Choose File
              </>
            )}
          </label>
          <input
            id="favicon-upload"
            type="file"
            accept=".ico,.png,.jpg,.jpeg,image/x-icon,image/vnd.microsoft.icon,image/png,image/jpeg"
            onChange={handleFaviconUpload}
            disabled={uploadingFavicon}
            style={{ display: 'none' }}
          />
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>
            Current: <img src="/favicon.ico" alt="favicon" style={{ width: '16px', height: '16px', display: 'inline-block', verticalAlign: 'middle', marginLeft: '4px' }} />
          </span>
        </div>
      </div>

      {/* Color Settings Grid */}
      <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        <ColorSettingItem
          settingKey="theme_primary_bg"
          title="Primary Background"
          description="Main panel background color (dark)"
          defaultColor="#0a0a0a"
        />

        <ColorSettingItem
          settingKey="theme_secondary_bg"
          title="Secondary Background"
          description="Box and card background color"
          defaultColor="#1a1a1a"
        />

        <ColorSettingItem
          settingKey="theme_outline_color"
          title="Outline Color"
          description="Border color for boxes and cards"
          defaultColor="#4a4a4a"
        />

        <ColorSettingItem
          settingKey="theme_color"
          title="Theme Color"
          description="Primary accent color (purple)"
          defaultColor="#3b198e"
        />
      </div>

      {/* Preview Section */}
      <div style={{ 
        border: '1px solid #4a4a4a', 
        borderRadius: '8px', 
        padding: '16px',
        backgroundColor: '#1a1a1a'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Palette className="h-5 w-5" style={{ color: '#3b198e' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff' }}>Color Preview</h3>
        </div>
        <div style={{ 
          backgroundColor: settings?.theme_primary_bg?.value || '#0a0a0a',
          border: `1px solid ${settings?.theme_outline_color?.value || '#4a4a4a'}`,
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: settings?.theme_secondary_bg?.value || '#1a1a1a',
            border: `1px solid ${settings?.theme_outline_color?.value || '#4a4a4a'}`,
            borderRadius: '6px',
            padding: '12px'
          }}>
            <p style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Sample Card Content</p>
            <button style={{
              padding: '6px 12px',
              backgroundColor: settings?.theme_color?.value || '#3b198e',
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: 'pointer'
            }}>
              Theme Button
            </button>
          </div>
        </div>
      </div>

      {/* Warning Notice */}
      <div style={{ 
        backgroundColor: 'rgba(234, 179, 8, 0.1)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Palette className="h-5 w-5" style={{ color: '#eab308' }} />
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#eab308', marginBottom: '8px' }}>
              Theme Notice
            </h3>
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>
              Changes to theme colors will reload the page to apply the new color scheme. 
              Make sure to save any unsaved work before updating colors.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;
